// Third Party
import isEqual from 'lodash/isEqual';

// Namespace
import * as ProposalNS from './Proposal';
import * as ProposalElementsNS from './ProposalElements';

// Types
import { DisclaimerConfig } from './DAOs/ProposalDAO';

// Utils
import { formatMessage, LOCALE } from '../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../utils/constants';
import { isValidGICItem } from './ProposalHelpers';
import Comparison from '@/types/comparison';
import { round } from 'lodash';
import ProposalOrientation from '@/types/proposal-orientation';
import { DEFAULT_LANDSCAPE_FRACTION, DEFAULT_PORTRAIT_FRACTION, PROPOSAL_ORIENTATION } from './utils/constants';
import { BMO_GIF_LSEG_MANUFACTURERS, BMO_GIF_LSEG_UNIVERSE } from '@/utils/bmo-gif/constants';

export class ProposalTemplateUtils {
  static getMissingRequirements(comparison: Comparison, template: ProposalNS.ProposalTemplateV2): ProposalNS.ProposalTemplateRequirements {
    return template.requirements.filter(requirement => {
      switch (requirement.key) {
        case ProposalNS.TemplateRequirementKeys.MinItems: {
          const items = comparison.items.filter(item => item.type === requirement.type);
          return items.length < requirement.value;
        }
        case ProposalNS.TemplateRequirementKeys.RiskProfile: {
          // Check if flag is true and the comparison does not have a risk period attached to it.
          return requirement.value && !comparison.riskProfile;
        }
        case ProposalNS.TemplateRequirementKeys.ComparisonPortfolioRiskProfile: {
          // Check if flag is true and that every portfolio in the comparison does not have a risk profile attached to it.
          // This is different from RiskProfile requirement, which checks only for the comparison risk profile.
          // We want to check also if all portfolios have a risk profile in the comparison
          if (requirement.value && comparison.riskProfile) {
            return false;
          }

          return (
            requirement.value &&
            comparison.items.some(item => {
              if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
                return false;
              }
              return !item.riskProfile;
            })
          );
        }
        case ProposalNS.TemplateRequirementKeys.MinGIFItems: {
          return !comparison.gifItems || comparison.gifItems.length < requirement.value;
        }
        case ProposalNS.TemplateRequirementKeys.MinGIFFunds: {
          const items = comparison.items.filter(item => {
            const isInsuranceFund = item.universe && item.universe[LOCALE.EN_CA] === BMO_GIF_LSEG_UNIVERSE;
            const isBMOManufacturer = item.manufacturer && BMO_GIF_LSEG_MANUFACTURERS.includes(item.manufacturer);

            return item.type === COMPARISON_ITEM_TYPES.FUND && isInsuranceFund && isBMOManufacturer;
          });
          return items.length < requirement.value;
        }
        case ProposalNS.TemplateRequirementKeys.MinGIFBenchmarks: {
          const items = comparison.items.filter(item => isValidGICItem(item, requirement.minRate, requirement.maxRate));
          return items.length < requirement.value;
        }
      }

      return true;
    });
  }

  private static formatMissingRequirements(missingRequirements: ProposalNS.ProposalTemplateRequirements): ProposalNS.FormattedProposalTemplateRequirements {
    const formattedMissingRequirement: ProposalNS.FormattedProposalTemplateRequirements = [];

    missingRequirements.forEach(requirement => {
      const { key } = requirement;
      switch (requirement.key) {
        case ProposalNS.TemplateRequirementKeys.MinItems: {
          formattedMissingRequirement.push({
            key,
            text: formatMessage(`ProposalRequirements.Options.MinItems.${requirement.type.toUpperCase()}`, {
              count: requirement.value,
            }),
          });
          break;
        }
        case ProposalNS.TemplateRequirementKeys.MinGIFItems: {
          formattedMissingRequirement.push({
            key,
            text: formatMessage('ProposalRequirements.Options.MinGifItems'),
          });
          break;
        }
        case ProposalNS.TemplateRequirementKeys.MinGIFFunds: {
          formattedMissingRequirement.push({
            key,
            text: formatMessage('ProposalRequirements.Options.MinGifFunds', {
              count: requirement.value,
            }),
          });
          break;
        }
        case ProposalNS.TemplateRequirementKeys.MinGIFBenchmarks: {
          formattedMissingRequirement.push({
            key,
            text: formatMessage('ProposalRequirements.Options.MinGifBenchmarks', {
              count: requirement.value,
              minRate: requirement.minRate !== undefined ? requirement.minRate.toString() : 0,
              maxRate: requirement.maxRate !== undefined ? requirement.maxRate.toString() : '',
            }),
          });
          break;
        }
        case ProposalNS.TemplateRequirementKeys.RiskProfile: {
          formattedMissingRequirement.push({
            key,
            text: formatMessage('ProposalRequirements.Options.RiskProfile'),
            actionText: formatMessage('ProposalRequirements.Actions.RiskProfile'),
          });
          break;
        }
        case ProposalNS.TemplateRequirementKeys.ComparisonPortfolioRiskProfile: {
          formattedMissingRequirement.push({
            key,
            text: formatMessage('ProposalRequirements.Options.PortfolioRiskProfile'),
          });
          break;
        }
        default:
          return requirement;
      }

      return null;
    });

    return formattedMissingRequirement;
  }

  /**
   * Retrieves the missing requirements for a single template
   * @param {Comparison} comparison current comparison
   * @param {ProposalNS.ProposalTemplateV2} template template to evaluate
   * @returns an array missing requirements
   */
  public static getFormattedMissingRequirements(comparison: Comparison, template: ProposalNS.ProposalTemplateV2) {
    const missingRequirements = ProposalTemplateUtils.getMissingRequirements(comparison, template);
    return this.formatMissingRequirements(missingRequirements);
  }

  /**
   * Retrieves the templates that have missing requirements
   * @param {Comparison} comparison current comparison
   * @param {ProposalNS.BaseProposalTemplate[]} templates list of user available templates
   * @returns an array of objects representing template keys and their missing requirements
   */
  public static getTemplatesWithFormattedMissingRequirements(
    comparison: Comparison,
    templates: ProposalNS.BaseProposalTemplate[],
  ): { templateKey: string; missingRequirements: ProposalNS.FormattedProposalTemplateRequirements }[] {
    const templatesWithMissingRequirements: { templateKey: string; missingRequirements: ProposalNS.FormattedProposalTemplateRequirements }[] = [];
    // TODO Remove this filter when we switch to v2 only
    const v2Templates = templates.filter(template => template.builderVersion === ProposalNS.ProposalBuilderVersion.V2);

    v2Templates.forEach(template => {
      const templateV2 = template as ProposalNS.ProposalTemplateV2;
      const formattedMissingRequirements = this.getFormattedMissingRequirements(comparison, templateV2);

      if (formattedMissingRequirements.length > 0) {
        templatesWithMissingRequirements.push({
          templateKey: templateV2.key,
          missingRequirements: formattedMissingRequirements,
        });
      }
    });

    return templatesWithMissingRequirements;
  }

  /**
   * Merges common page disclaimers and moves them to the lowest section possible
   * @param {ProposalElementsNS.ProposalSectionElement[][]} pages proposal pages
   */
  public static mergeAndSinkDisclaimers(pages: ProposalElementsNS.ProposalSectionElement[][]): void {
    for (const page of pages) {
      // we will track the disclaimer configs already in the page here.
      const pageDisclaimers: DisclaimerConfig[] = [];
      const nbSections = page.length;

      // parse in reverse order - repeating disclaimers will show in the bottom section
      for (let i = nbSections - 1; i >= 0; i--) {
        const section = page[i];
        const sectionDisclaimers: DisclaimerConfig[] | undefined = section.disclaimerConfigs;

        if (sectionDisclaimers !== undefined) {
          const tmpDisclaimers = [];

          for (const sectionDisclaimer of sectionDisclaimers) {
            let foundDisclaimer: DisclaimerConfig | undefined;
            if (sectionDisclaimer.config.doNotMergeOnPage !== true) {
              // eslint-disable-next-line no-loop-func
              foundDisclaimer = pageDisclaimers.find(
                pageDisclaimer =>
                  pageDisclaimer.key != null && pageDisclaimer.key === sectionDisclaimer.key && isEqual(pageDisclaimer.config, sectionDisclaimer.config),
              );
            }

            if (foundDisclaimer === undefined) {
              // no disclaimer config for this key simply add it.
              tmpDisclaimers.push(sectionDisclaimer);
            } else {
              // we found a config in the page for this key, same items?
              const pageItems = foundDisclaimer.items;
              // filter out the items the page already has
              const items = sectionDisclaimer.items.filter(item => !pageItems.some(pageItem => pageItem._id === item._id));
              if (items.length > 0) {
                for (const item of items) {
                  // add items to the found disclaimer (this is where the merge actually happens)
                  foundDisclaimer.items.push(item);
                }
              }
            }
          }

          for (const tmp of tmpDisclaimers) {
            // persiste the page disclaimer for lookup in other sections
            pageDisclaimers.push(tmp);
          }
          // update the section's disclaimers.
          section.disclaimerConfigs = tmpDisclaimers;
        }
      }
    }
  }

  /**
   * Generates an object containing the start and end page number for each section currently visible in the proposal
   * @param {ProposalElementsNS.ProposalSectionElement[][]} pages proposal pages
   * @returns a dictionary that defines the start and end page number of each section
   */
  public static generateSectionPageIndexes(sectionPagesSplit: ProposalElementsNS.ProposalSectionElement[][]): ProposalNS.ProposalSectionPageMapping {
    const pageSectionMapping = sectionPagesSplit.reduce((acc: string[][], page) => [...acc, page.map(section => section.sectionKey)], []);
    const pageIndexes = pageSectionMapping.reduce(
      (sectionPages: { [key: string]: { start: number; end: number | null } }, page, currentIndex) => ({
        ...sectionPages,
        ...page.reduce((acc, sectionKey) => {
          // If the "acc" accumulator already contains the sectionKey
          // it means we already processed the start page of that section
          // and we should "upsert" the end page of that section
          if (Object.keys(sectionPages).includes(sectionKey)) {
            return {
              ...acc,
              [sectionKey]: {
                ...sectionPages[sectionKey],
                end: currentIndex + 1,
              },
            };
          }

          // Else if the accumulator doesn't contains the sectionKey
          // it means it's the first time we process that sectionKey
          // and the start page should be populated
          return {
            ...acc,
            [sectionKey]: {
              start: currentIndex + 1,
              end: null,
            },
          };
        }, {}),
      }),
      {},
    );

    return pageIndexes;
  }

  /**
   * Calculates the remaining fraction of a proposal page that is not used by the given sections.
   *
   * @param page - An array of proposal section elements on the page.
   * @param orientation - The orientation of the proposal (e.g., portrait or landscape).
   * @param hiddenLayouts - An array of layout identifiers that should be considered hidden.
   * @returns The remaining fraction of the page that is not used.
   */
  public static calculateRemainingProposalPageFraction = (
    page: ProposalElementsNS.ProposalSectionElement[],
    orientation: ProposalOrientation,
    hiddenLayouts: string[],
  ) => {
    const usedFraction = page.reduce(
      (sum: number, sectionElement: ProposalElementsNS.ProposalSectionElement) =>
        round(sum + this.getSectionElementPageFraction(sectionElement, orientation, hiddenLayouts), 4),
      0,
    );

    return round(1 - usedFraction, 4);
  };

  /**
   * Calculates the total fraction of the layout that is hidden based on the provided hidden layouts.
   *
   * @param element - The proposal section element containing hideable layouts.
   * @param orientation - The orientation of the proposal (e.g., 'portrait' or 'landscape').
   * @param hiddenLayouts - An array of layout keys that are hidden.
   * @returns The total fraction of the layout that is hidden.
   */
  public static calculateHiddenLayoutFraction = (
    element: ProposalElementsNS.ProposalSectionElement,
    orientation: ProposalOrientation,
    hiddenLayouts: string[],
  ): number => {
    if (!element.hideableLayouts || !hiddenLayouts) {
      return 0;
    }
    return round(
      element.hideableLayouts
        .filter(layout => hiddenLayouts.includes(layout.key))
        .map(layout => layout.pageFraction[orientation] ?? 0)
        .reduce((sum, fraction) => sum + fraction, 0),
      4,
    );
  };

  /**
   * Calculates the page fraction of a given proposal section element based on its orientation and hidden layouts.
   *
   * @param element - The proposal section element to calculate the page fraction for.
   * @param orientation - The orientation of the proposal (portrait or landscape).
   * @param hiddenLayouts - An array of layout identifiers that are hidden.
   * @returns The calculated page fraction of the element, adjusted for hidden layouts.
   */
  public static getSectionElementPageFraction = (
    element: ProposalElementsNS.ProposalSectionElement,
    orientation: ProposalOrientation,
    hiddenLayouts: string[],
  ): number => {
    const defaultFraction = orientation === PROPOSAL_ORIENTATION.PORTRAIT ? DEFAULT_PORTRAIT_FRACTION : DEFAULT_LANDSCAPE_FRACTION;
    const hiddenFraction = this.calculateHiddenLayoutFraction(element, orientation, hiddenLayouts);

    return round(element.pageFraction[orientation] - hiddenFraction || defaultFraction, 4);
  };
}
